import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import courseContent from './courseContent';
import { supabase } from './supabaseClient';


/**
 * Random helpers (Fisher–Yates) to avoid answer-position bias (e.g., always B/C).
 * Shuffles arrays and shuffles each question's options while keeping correctAnswer aligned.
 */
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const shuffleQuestionOptions = (q) => {
  // Course questions are shaped like:
  // { question, options: [...], correctAnswer: <index>, explanation, relatedSection }
  if (!q || !Array.isArray(q.options) || typeof q.correctAnswer !== 'number') return q;

  const indexed = q.options.map((opt, i) => ({ opt, i }));
  const shuffled = shuffleArray(indexed);
  const newCorrect = shuffled.findIndex(x => x.i === q.correctAnswer);

  return {
    ...q,
    options: shuffled.map(x => x.opt),
    correctAnswer: newCorrect
  };
};

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState('signin'); // 'signin', 'signup', or 'forgot'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Progress state
  const [progress, setProgress] = useState({
    sectionsRead: [], // ["1-0", "1-1", "2-0"] format: chapterId-sectionIndex
    quizScores: {}    // { "1": 90, "2": 80 } format: chapterId: bestScore
  });

  // App state
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    selectedAnswer: null,
    showExplanation: false,
    score: 0,
    completed: false,
    shuffledQuestions: []
  });
  
  const contentRef = useRef(null);

  // Check for existing session on mount
  useEffect(() => {
    // Check for recovery flag (set by index.html before Supabase clears hash)
    if (sessionStorage.getItem('passwordRecovery')) {
      sessionStorage.removeItem('passwordRecovery');
      setShowPasswordReset(true);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProgress(session.user.id);
      }
      setInitialLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProgress(session.user.id);
      }
      if (event === 'PASSWORD_RECOVERY') {
        setShowPasswordReset(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load progress from Supabase
  const loadProgress = async (userId) => {
    const { data, error } = await supabase
      .from('user_progress')
      .select('sections_read, quiz_scores')
      .eq('user_id', userId)
      .single();

    if (data) {
      setProgress({
        sectionsRead: data.sections_read || [],
        quizScores: data.quiz_scores || {}
      });
    }
  };

  // Save progress to Supabase
  const saveProgress = async (newProgress) => {
    if (!user) return;

    const { error } = await supabase
      .from('user_progress')
      .upsert({
        user_id: user.id,
        sections_read: newProgress.sectionsRead,
        quiz_scores: newProgress.quizScores,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) console.error('Error saving progress:', error);
  };

  // Mark section as read
  const markSectionRead = (chapterId, sectionIndex) => {
    const key = `${chapterId}-${sectionIndex}`;
    if (!progress.sectionsRead.includes(key)) {
      const newProgress = {
        ...progress,
        sectionsRead: [...progress.sectionsRead, key]
      };
      setProgress(newProgress);
      saveProgress(newProgress);
    }
  };

  // Save quiz score (only if better than previous)
  const saveQuizScore = (chapterId, score) => {
    const currentBest = progress.quizScores[chapterId] || 0;
    if (score > currentBest) {
      const newProgress = {
        ...progress,
        quizScores: { ...progress.quizScores, [chapterId]: score }
      };
      setProgress(newProgress);
      saveProgress(newProgress);
    }
  };

  // Auth handlers
  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      if (authMode === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(authEmail, {
          redirectTo: window.location.origin + '?reset=1'
        });
        if (error) throw error;
        setAuthError('Check your email for the password reset link!');
      } else if (authMode === 'signup') {
        const { error } = await supabase.auth.signUp({
          email: authEmail,
          password: authPassword
        });
        if (error) throw error;
        setAuthError('Check your email for the confirmation link!');
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: authEmail,
          password: authPassword
        });
        if (error) throw error;
      }
    } catch (error) {
      setAuthError(error.message || error.error_description || JSON.stringify(error));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setProgress({ sectionsRead: [], quizScores: {} });
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setShowPasswordReset(false);
      setNewPassword('');
      setAuthError('Password updated successfully!');
    } catch (error) {
      setAuthError(error.message || JSON.stringify(error));
    } finally {
      setAuthLoading(false);
    }
  };

  // Scroll to top when content changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSection, activeQuiz, quizState.currentQuestion]);

  const toggleChapter = (chapterId) => {
    if (expandedChapter === chapterId) {
      setExpandedChapter(null);
    } else {
      setExpandedChapter(chapterId);
      selectSection(chapterId, 0);
    }
  };

  const selectSection = (chapterId, sectionIndex) => {
    setActiveSection({ chapterId, sectionIndex });
    setActiveQuiz(null);
    setQuizState({
      currentQuestion: 0,
      selectedAnswer: null,
      showExplanation: false,
      score: 0,
      completed: false,
      shuffledQuestions: []
    });
    // REMOVED: Auto-mark as read - now requires manual completion
  };

  const startQuiz = (chapterId) => {
    const chapter = courseContent.chapters.find(c => c.id === chapterId);
    if (chapter && chapter.quiz) {
      const shuffled = shuffleArray(chapter.quiz.questions)
        .slice(0, 10)
        .map(shuffleQuestionOptions);
      
      setActiveQuiz(chapterId);
      setActiveSection(null);
      setQuizState({
        currentQuestion: 0,
        selectedAnswer: null,
        showExplanation: false,
        score: 0,
        completed: false,
        shuffledQuestions: shuffled
      });
    }
  };

  const handleAnswer = (answerIndex) => {
    if (quizState.showExplanation) return;
    setQuizState(prev => ({ ...prev, selectedAnswer: answerIndex }));
  };

  const submitAnswer = () => {
    if (quizState.selectedAnswer === null) return;
    
    const currentQ = quizState.shuffledQuestions[quizState.currentQuestion];
    const isCorrect = quizState.selectedAnswer === currentQ.correctAnswer;
    
    setQuizState(prev => ({
      ...prev,
      showExplanation: true,
      score: isCorrect ? prev.score + 1 : prev.score
    }));
  };

  const nextQuestion = () => {
    if (quizState.currentQuestion + 1 >= quizState.shuffledQuestions.length) {
      const finalScore = Math.round((quizState.score / quizState.shuffledQuestions.length) * 100);
      saveQuizScore(activeQuiz, finalScore);
      setQuizState(prev => ({ ...prev, completed: true }));
    } else {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        selectedAnswer: null,
        showExplanation: false
      }));
    }
  };

  const retakeQuiz = () => {
    startQuiz(activeQuiz);
  };

  const navigateSection = (direction) => {
    if (!activeSection) return;
    
    const chapter = courseContent.chapters.find(c => c.id === activeSection.chapterId);
    if (!chapter) return;
    
    const newIndex = activeSection.sectionIndex + direction;
    
    if (newIndex >= 0 && newIndex < chapter.sections.length) {
      // Navigate within current chapter
      selectSection(activeSection.chapterId, newIndex);
    } else if (newIndex < 0 && activeSection.chapterId > 1) {
      // Go to previous chapter's last section
      const prevChapter = courseContent.chapters.find(c => c.id === activeSection.chapterId - 1);
      if (prevChapter) {
        setExpandedChapter(prevChapter.id);
        selectSection(prevChapter.id, prevChapter.sections.length - 1);
      }
    } else if (newIndex >= chapter.sections.length) {
      // At end of chapter - go to quiz instead of next chapter
      if (chapter.quiz) {
        startQuiz(chapter.id);
      } else {
        // No quiz for this chapter, go to next chapter
        const nextChapter = courseContent.chapters.find(c => c.id === activeSection.chapterId + 1);
        if (nextChapter) {
          setExpandedChapter(nextChapter.id);
          selectSection(nextChapter.id, 0);
        }
      }
    }
  };

  // Calculate overall progress
  const totalSections = courseContent.chapters.reduce((sum, ch) => sum + ch.sections.length, 0);
  const progressPercent = Math.round((progress.sectionsRead.length / totalSections) * 100);

  // Check if section is read
  const isSectionRead = (chapterId, sectionIndex) => {
    return progress.sectionsRead.includes(`${chapterId}-${sectionIndex}`);
  };

  // Get chapter progress
  const getChapterProgress = (chapter) => {
    const readCount = chapter.sections.filter((_, idx) => 
      progress.sectionsRead.includes(`${chapter.id}-${idx}`)
    ).length;
    return { read: readCount, total: chapter.sections.length };
  };

  // Loading screen
  if (initialLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Auth screen
  if (!user) {
    return (
      <div className="auth-screen">
        <div className="auth-container">
          <h1>SD P&C Insurance</h1>
          <p className="auth-subtitle">Pre-Licensing Course</p>
          
          <form onSubmit={handleAuth} className="auth-form">
            {authMode !== 'forgot' ? (
              <div className="auth-tabs">
                <button
                  type="button"
                  className={`auth-tab ${authMode === 'signin' ? 'active' : ''}`}
                  onClick={() => setAuthMode('signin')}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  className={`auth-tab ${authMode === 'signup' ? 'active' : ''}`}
                  onClick={() => setAuthMode('signup')}
                >
                  Sign Up
                </button>
              </div>
            ) : (
              <h2 className="auth-heading">Reset Password</h2>
            )}

            <input
              type="email"
              placeholder="Email"
              value={authEmail}
              onChange={(e) => setAuthEmail(e.target.value)}
              autoComplete="email"
              required
            />
            {authMode !== 'forgot' && (
              <input
                type="password"
                placeholder="Password"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                required
                minLength={6}
              />
            )}

            {authError && <p className={`auth-error ${authError.includes('Check your email') ? 'success' : ''}`}>{authError}</p>}

            <button type="submit" className="auth-submit" disabled={authLoading}>
              {authLoading ? 'Loading...' : (
                authMode === 'forgot' ? 'Send Reset Link' :
                authMode === 'signin' ? 'Sign In' : 'Create Account'
              )}
            </button>

            {authMode === 'signin' && (
              <button
                type="button"
                className="forgot-link"
                onClick={() => { setAuthMode('forgot'); setAuthError(''); }}
              >
                Forgot password?
              </button>
            )}
            {authMode === 'forgot' && (
              <button
                type="button"
                className="forgot-link"
                onClick={() => { setAuthMode('signin'); setAuthError(''); }}
              >
                ← Back to Sign In
              </button>
            )}
          </form>

          <p className="auth-note">
            Your progress will be saved and synced across devices.
          </p>
        </div>
      </div>
    );
  }

  const renderWelcome = () => (
    <div className="welcome-screen">
      <div className="welcome-content">
        <h1>{courseContent.title}</h1>
        <p className="subtitle">{courseContent.subtitle}</p>
        <div className="welcome-info">
          <div className="info-card">
            <span className="info-number">{courseContent.chapters.length}</span>
            <span className="info-label">Chapters</span>
          </div>
          <div className="info-card">
            <span className="info-number">450</span>
            <span className="info-label">Quiz Questions</span>
          </div>
          <div className="info-card">
            <span className="info-number">{progressPercent}%</span>
            <span className="info-label">Complete</span>
          </div>
        </div>
        <p className="welcome-cta">Select a chapter from the sidebar to begin studying.</p>
      </div>
    </div>
  );

  const renderSection = () => {
    if (!activeSection) return null;
    
    const chapter = courseContent.chapters.find(c => c.id === activeSection.chapterId);
    if (!chapter) return null;
    
    const section = chapter.sections[activeSection.sectionIndex];
    if (!section) return null;
    
    const isFirst = activeSection.sectionIndex === 0 && activeSection.chapterId === 1;
    const isLastSection = activeSection.sectionIndex === chapter.sections.length - 1;
    const isLastChapter = activeSection.chapterId === courseContent.chapters.length;
    const isVeryLast = isLastSection && isLastChapter && !chapter.quiz;
    const sectionKey = `${activeSection.chapterId}-${activeSection.sectionIndex}`;
    const isRead = progress.sectionsRead.includes(sectionKey);
    
    return (
      <div className="section-view">
        <div className="section-header">
          <span className="chapter-badge">Chapter {chapter.id}</span>
          <h2>{section.title}</h2>
        </div>
        <div 
          className="section-content"
          dangerouslySetInnerHTML={{ __html: section.content }}
        />
        
        {/* Mark Complete Button */}
        <div className="section-complete-container">
          {isRead ? (
            <div className="section-completed-badge">
              <span className="completed-check">✓</span>
              Section Completed
            </div>
          ) : (
            <button 
              className="mark-complete-btn"
              onClick={() => markSectionRead(activeSection.chapterId, activeSection.sectionIndex)}
            >
              ✓ Mark Section Complete
            </button>
          )}
        </div>
        
        <div className="section-nav">
          <button 
            className="nav-btn prev"
            onClick={() => navigateSection(-1)}
            disabled={isFirst}
          >
            ← Previous
          </button>
          <span className="section-indicator">
            Section {activeSection.sectionIndex + 1} of {chapter.sections.length}
          </span>
          <button 
            className="nav-btn next"
            onClick={() => navigateSection(1)}
            disabled={isVeryLast}
          >
            {isLastSection && chapter.quiz ? 'Take Quiz →' : 'Next →'}
          </button>
        </div>
      </div>
    );
  };

  const renderQuiz = () => {
    if (!activeQuiz) return null;
    
    const chapter = courseContent.chapters.find(c => c.id === activeQuiz);
    if (!chapter) return null;
    
    if (quizState.completed) {
      const percentage = Math.round((quizState.score / quizState.shuffledQuestions.length) * 100);
      const passed = percentage >= 80;
      const bestScore = progress.quizScores[activeQuiz];
      
      return (
        <div className="quiz-view">
          <div className="quiz-header">
            <span className="chapter-badge">Chapter {chapter.id}</span>
            <h2>Quiz Complete</h2>
          </div>
          <div className="quiz-results">
            <div className={`score-circle ${passed ? 'passed' : 'failed'}`}>
              <span className="score-number">{percentage}%</span>
              <span className="score-label">{quizState.score}/{quizState.shuffledQuestions.length}</span>
            </div>
            <p className={`result-message ${passed ? 'passed' : 'failed'}`}>
              {passed ? 'Great job! You passed!' : 'Keep studying and try again.'}
            </p>
            {bestScore && (
              <p className="best-score">Your best score: {bestScore}%</p>
            )}
            <button className="retake-btn" onClick={retakeQuiz}>
              Retake Quiz
            </button>
          </div>
        </div>
      );
    }
    
    const currentQ = quizState.shuffledQuestions[quizState.currentQuestion];
    if (!currentQ) return null;
    
    return (
      <div className="quiz-view">
        <div className="quiz-header compact">
          <div className="quiz-header-top">
            <span className="chapter-badge">Chapter {chapter.id} Quiz</span>
            <span className="quiz-progress">
              {quizState.currentQuestion + 1} / {quizState.shuffledQuestions.length}
            </span>
          </div>
        </div>
        
        <div className="quiz-question-area">
          <p className="question-text">{currentQ.question}</p>
          
          <div className="answers-list">
            {currentQ.options.map((option, idx) => (
              <button
                key={idx}
                className={`answer-btn 
                  ${quizState.selectedAnswer === idx ? 'selected' : ''}
                  ${quizState.showExplanation ? (
                    idx === currentQ.correctAnswer ? 'correct' : 
                    quizState.selectedAnswer === idx ? 'incorrect' : ''
                  ) : ''}`}
                onClick={() => handleAnswer(idx)}
                disabled={quizState.showExplanation}
              >
                <span className="answer-letter">{String.fromCharCode(65 + idx)}</span>
                <span className="answer-text">{option}</span>
              </button>
            ))}
          </div>
          
          {!quizState.showExplanation ? (
            <button 
              className="submit-btn"
              onClick={submitAnswer}
              disabled={quizState.selectedAnswer === null}
            >
              Submit Answer
            </button>
          ) : (
            <div className="explanation-box">
              <div className={`explanation-header ${quizState.selectedAnswer === currentQ.correctAnswer ? 'correct' : 'incorrect'}`}>
                {quizState.selectedAnswer === currentQ.correctAnswer ? '✓ Correct!' : '✗ Incorrect'}
              </div>
              <p className="explanation-text">{currentQ.explanation}</p>
              <button className="continue-btn" onClick={nextQuestion}>
                {quizState.currentQuestion + 1 >= quizState.shuffledQuestions.length ? 'See Results' : 'Continue →'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>SD P&C Insurance</h1>
          <p>Pre-Licensing Course</p>
          <div className="user-info">
            <span className="user-email">{user.email}</span>
            <button className="sign-out-btn" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>

        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>
          <span className="progress-text">{progressPercent}% Complete</span>
        </div>
        
        <nav className="chapter-nav">
          {courseContent.chapters.map(chapter => {
            const chapterProg = getChapterProgress(chapter);
            const quizScore = progress.quizScores[chapter.id];
            
            return (
              <div key={chapter.id} className="chapter-item">
                <button 
                  className={`chapter-header ${expandedChapter === chapter.id ? 'expanded' : ''}`}
                  onClick={() => toggleChapter(chapter.id)}
                >
                  <span className="chapter-number">{chapter.id}</span>
                  <span className="chapter-title">
                    {chapter.title}
                    <span className="chapter-progress-mini">
                      {chapterProg.read}/{chapterProg.total}
                      {quizScore && <span className="quiz-score-mini">Quiz: {quizScore}%</span>}
                    </span>
                  </span>
                  <span className="expand-icon">{expandedChapter === chapter.id ? '−' : '+'}</span>
                </button>
                
                {expandedChapter === chapter.id && (
                  <div className="chapter-sections">
                    {chapter.sections.map((section, idx) => (
                      <button
                        key={idx}
                        className={`section-btn ${activeSection?.chapterId === chapter.id && activeSection?.sectionIndex === idx ? 'active' : ''} ${isSectionRead(chapter.id, idx) ? 'read' : ''}`}
                        onClick={() => selectSection(chapter.id, idx)}
                      >
                        {isSectionRead(chapter.id, idx) && <span className="read-check">✓</span>}
                        {section.title}
                      </button>
                    ))}
                    {chapter.quiz && (
                      <button
                        className={`quiz-btn ${activeQuiz === chapter.id ? 'active' : ''}`}
                        onClick={() => startQuiz(chapter.id)}
                      >
                        📝 Take Quiz {quizScore && `(Best: ${quizScore}%)`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
      
      <main className="main-content" ref={contentRef}>
        {!activeSection && !activeQuiz && renderWelcome()}
        {activeSection && renderSection()}
        {activeQuiz && renderQuiz()}
      </main>

      {showPasswordReset && (
        <div className="password-reset-overlay">
          <div className="password-reset-modal">
            <h2>Set New Password</h2>
            <form onSubmit={handlePasswordUpdate}>
              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={6}
              />
              {authError && <p className={`auth-error ${authError.includes('successfully') ? 'success' : ''}`}>{authError}</p>}
              <button type="submit" className="auth-submit" disabled={authLoading}>
                {authLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
